/**
 * The Thread's generic tool-row renderer (`components.ToolFallback`), wrapped so the
 * expandable body is privileged (UX-6 — see {@link useToolDetailsVisible} for the policy
 * and the reasoning).
 *
 * Rows render inside the message's execution panel, one per tool call, and only once the
 * reader has asked for "details and tools" — the panel's step list already names every
 * tool that ran, for everyone.
 *
 * Regular users get the row's IDENTITY and nothing else: the same icon, the same
 * "Used tool: instrument-search_standard" line, the same duration — but inert, with no
 * chevron and nothing to open. Root and impersonated sessions get the unchanged
 * {@link ToolFallback}, arguments and result included.
 *
 * This only covers the fallback. Registry (`components.Tools`) renderers — charts, the
 * interactive choice pickers — are the user-facing output of a tool, not its internals, and
 * are untouched.
 */
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { ToolFallback } from "@mdotm/shadcn/assistant-ui";
import { isPendingTaskOutput } from "./conversationSync";
import { useToolDetailsVisible } from "./toolDetails";

export const SphereAIToolFallback: ToolCallMessagePartComponent = (props) => {
	const detailsVisible = useToolDetailsVisible();
	// A background task's call is answered with an interim ack while the work runs on the
	// server; the row stays "running" until the stored result replaces it (conversationSync.ts).
	const part = isPendingTaskOutput(props.result) ? { ...props, status: { type: "running" as const } } : props;

	// An approval gate lives INSIDE the collapsible body (Allow/Deny), so collapsing the row
	// away would park the turn with no way to answer it. Nothing in Sphere AI currently
	// reaches this state — sphere-mcp answers every tool call itself — but a
	// deadlocked chat is not an acceptable failure mode for a cosmetic gate.
	if (detailsVisible || part.status?.type === "requires-action") {
		return <ToolFallback {...part} />;
	}

	// No `data-qualifier` here on purpose: both modes carry `data-slot="tool-fallback-trigger"`
	// (the inert one additionally `data-expandable="false"`), so a test can find the line —
	// and the same selector works whichever role it runs as. A qualifier only the regular-user
	// row had would silently miss the privileged one.
	return (
		<ToolFallback.Trigger toolName={part.toolName} status={part.status} isError={part.isError} expandable={false} />
	);
};
